<template>
  <div class="tp" data-test="template-picker">
    <div v-if="searchable" class="tp__search-wrap">
      <i class="fa fa-search tp__search-ic" aria-hidden="true"></i>
      <input
        v-model="query"
        type="text"
        class="tp__search"
        autocomplete="off"
        :placeholder="searchPlaceholder"
        :aria-label="searchPlaceholder"
        data-test="template-picker-search"
      />
    </div>

    <!-- A note for the person who is about to search or scroll (e.g. "X isn't listed here"): between the
         search box and the rows, so it is seen without scrolling a long list. Nothing renders when unused. -->
    <slot name="above-list"></slot>

    <div class="tp__list" role="group" :aria-label="ariaLabel || searchPlaceholder">
      <button
        v-for="it in shown"
        :key="it.id"
        type="button"
        class="tp__row"
        :class="{ 'is-on': isOn(it.id) }"
        :role="multiple ? 'checkbox' : 'radio'"
        :aria-checked="isOn(it.id) ? 'true' : 'false'"
        :data-test="`tp-row-${it.id}`"
        @click="$emit('toggle', it.id)"
      >
        <span class="tp__mark" :class="multiple ? 'tp__mark--check' : 'tp__mark--radio'" aria-hidden="true">
          <i v-if="multiple && isOn(it.id)" class="fa fa-check"></i>
        </span>
        <span class="tp__text">
          <span class="tp__name">{{ it.name }}</span>
          <span v-if="it.desc" class="tp__desc">{{ it.desc }}</span>
        </span>
        <span v-if="it.badge" class="tp__badge" :class="`tp__badge--${it.badgeTone || 'slate'}`" :data-test="`tp-badge-${it.id}`">{{ it.badge }}</span>
      </button>
    </div>

    <p v-if="!matches.length" class="tp__empty" data-test="template-picker-empty">
      {{ query.trim() ? `Nothing matches “${query.trim()}”.` : emptyText }}
    </p>

    <button
      v-if="hiddenCount > 0"
      type="button"
      class="tp__more"
      data-test="template-picker-more"
      @click="showAll = true"
    >Show all {{ matches.length }}</button>
  </div>
</template>

<script>
/**
 * A searchable list of things to pick — forms and packets, Wiki templates, SMS templates, email
 * templates (design "Patient Action Panels" 2c / 2d / 2e). It replaces the paginated v-client-tables
 * the classic send popup drew: one list, a search box on top, rows that are at least 48px on touch.
 *
 * The parent owns the selection. This component draws `selected` (a list of item ids) and emits
 * `toggle` with the tapped item's id — the parent decides what that means (add / remove for a
 * multi-select, replace / clear for a single choice), so the classic selection methods keep running.
 *
 *   items   [{ id, name, desc?, badge?, badgeTone? }]    badgeTone 'blue' | 'slate' | 'green'
 *   multiple  checkboxes (true) or radios (false)
 *   limit   show this many rows then a "Show all N" link (0 = every row); a search shows every match
 *   slot `above-list`  optional note drawn between the search box and the rows
 *
 * Presentational: no store, no mixin. ⛔ No prop named patNum / siteID / loading / allSettings.
 */
export default {
  name: 'TemplatePicker',

  props: {
    items: { type: Array, default: () => [] },
    selected: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    searchable: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Search' },
    ariaLabel: { type: String, default: '' },
    emptyText: { type: String, default: 'Nothing to pick yet.' },
    limit: { type: Number, default: 0 },
  },

  data() {
    return { query: '', showAll: false };
  },

  computed: {
    matches() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.items;
      return this.items.filter(it => String(it.name || '').toLowerCase().includes(q)
        || String(it.desc || '').toLowerCase().includes(q));
    },
    capped() {
      return this.limit > 0 && !this.showAll && !this.query.trim();
    },
    shown() {
      return this.capped ? this.matches.slice(0, this.limit) : this.matches;
    },
    hiddenCount() {
      return this.capped ? Math.max(0, this.matches.length - this.limit) : 0;
    },
  },

  methods: {
    isOn(id) {
      return this.selected.includes(id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './action-panel';

.tp { display: flex; flex-direction: column; gap: 10px; }

.tp__search-wrap { position: relative; }

.tp__search-ic {
  position: absolute;
  top: 50%;
  left: 13px;
  transform: translateY(-50%);
  font-size: 13px;
  color: $pp-ink-4;
  pointer-events: none;
}

.tp__search {
  @include ap-field;
  padding-left: 36px;
}

.tp__list { display: flex; flex-direction: column; gap: 2px; }

.tp__row {
  @include ap-row;
  width: 100%;
  border-color: transparent;
  font: inherit;
  font-size: 14px;

  &:hover { background: $pp-bg; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: -2px; }

  &.is-on {
    background: $pp-brand-soft;
    border-color: $pp-brand-tint;
  }
}

.tp__mark {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1.5px solid $neutral-300;
  background: $pp-surface;
  color: #fff;
  font-size: 11px;
}

.tp__mark--check { border-radius: 6px; }
.tp__mark--radio { border-radius: 50%; }

.tp__row.is-on .tp__mark {
  border-color: $pp-brand-deep;
  background: $pp-brand-deep;
}

.tp__row.is-on .tp__mark--radio {
  background: $pp-surface;
  box-shadow: inset 0 0 0 4px $pp-brand-deep;
}

.tp__text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 1px; }

.tp__name {
  font-weight: 600;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.tp__desc {
  font-size: 12.5px;
  line-height: 1.35;
  color: $pp-ink-3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tp__badge {
  flex: none;
  padding: 2px 8px;
  border-radius: $pp-radius-pill;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.tp__badge--blue { background: $pp-blue-bg; color: $pp-blue-ink; }
.tp__badge--slate { background: $pp-slate-bg; color: $pp-slate-ink; }
.tp__badge--green { background: $pp-green-bg; color: $pp-green-ink; }

.tp__empty {
  margin: 4px 0 0;
  padding: 16px 0;
  text-align: center;
  font-size: 13.5px;
  color: $pp-ink-3;
}

.tp__more {
  align-self: flex-start;
  padding: 6px 0;
  border: 0;
  background: transparent;
  color: $pp-brand;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; }

  @include ap-touch { min-height: $ap-row-h-touch; }
}
</style>
